import { AccountSyncOperation } from '@Lib/Services/Sync/Account/Operation';
import { Uuids, extendArray, isNotUndefined, isNullOrUndefined, removeFromIndex, sleep, subtractFromArray, } from '@standardnotes/utils';
import { OfflineSyncOperation } from '@Lib/Services/Sync/Offline/Operation';
import { SNLog } from '@Lib/Log';
import { ServerSyncResponseResolver } from '@Lib/Services/Sync/Account/ResponseResolver';
import { SyncSignal } from '@Lib/Services/Sync/Signals';
import { PayloadSource, CreateDecryptedItemFromPayload, FilterDisallowedRemotePayloadsAndMap, DeltaOutOfSync, ImmutablePayloadCollection, CreatePayload, isEncryptedPayload, isDecryptedPayload, DecryptedPayload, CreateEncryptedServerSyncPushPayload, isDeletedItem, CreatePayloadSplit, CreateDeletedServerSyncPushPayload, CreateNonDecryptedPayloadSplit, DeltaOfflineSaved, PayloadEmitSource, getIncrementedDirtyIndex, getCurrentDirtyIndex, ItemMutator, isDecryptedOrDeletedItem, MutationType, } from '@standardnotes/models';
import { AbstractService, SyncEvent, SyncSource, StorageKey, IntegrityEvent, SyncMode, SyncQueueStrategy, isFullEntryLoadChunkResponse, isChunkFullEntry, SyncOpStatus, WebSocketsServiceEvent, } from '@standardnotes/services';
import { CreateDecryptionSplitWithKeyLookup, CreateEncryptionSplitWithKeyLookup, SplitPayloadsByEncryptionType, } from '@standardnotes/encryption';
import { CreatePayloadFromRawServerItem } from './Account/Utilities';
import { ContentType } from '@standardnotes/domain-core';
const DEFAULT_MAJOR_CHANGE_THRESHOLD = 15;
const INVALID_SESSION_RESPONSE_STATUS = 401;
const TOO_MANY_REQUESTS_RESPONSE_STATUS = 429;
const DEFAULT_AUTO_SYNC_INTERVAL = 30000;
/** Content types appearing first are always mapped first */
const ContentTypeLocalLoadPriorty = [
    ContentType.TYPES.ItemsKey,
    ContentType.TYPES.KeySystemRootKey,
    ContentType.TYPES.KeySystemItemsKey,
    ContentType.TYPES.VaultListing,
    ContentType.TYPES.TrustedContact,
    ContentType.TYPES.UserPrefs,
    ContentType.TYPES.Component,
    ContentType.TYPES.Theme,
];
/**
 * The sync service orchestrates with the model manager, api service, and storage service
 * to ensure consistent state between the three. When a change is made to an item, consumers
 * call the sync service's sync function to first persist pending changes to local storage.
 * Then, the items are uploaded to the server. The sync service handles server responses,
 * including mapping any retrieved items to application state via model manager mapping.
 * After each sync request, any changes made or retrieved are also persisted locally.
 * The sync service largely does not perform any task unless it is called upon.
 */
export class SyncService extends AbstractService {
    constructor(itemManager, sessionManager, encryptionService, storageService, payloadManager, apiService, historyService, device, identifier, options, logger, sockets, syncFrequencyGuard, syncBackoffService, internalEventBus) {
        super(internalEventBus);
        this.itemManager = itemManager;
        this.sessionManager = sessionManager;
        this.encryptionService = encryptionService;
        this.storageService = storageService;
        this.payloadManager = payloadManager;
        this.apiService = apiService;
        this.historyService = historyService;
        this.device = device;
        this.identifier = identifier;
        this.options = options;
        this.logger = logger;
        this.sockets = sockets;
        this.syncFrequencyGuard = syncFrequencyGuard;
        this.syncBackoffService = syncBackoffService;
        this.internalEventBus = internalEventBus;
        this.outOfSync = false;
        this.resolveQueue = [];
        this.spawnQueue = [];
        /* A DownloadFirst sync must always be the first sync completed */
        this.completedOnlineDownloadFirstSync = false;
        this.majorChangeThreshold = DEFAULT_MAJOR_CHANGE_THRESHOLD;
        this.clientLocked = false;
        this.databaseLoaded = false;
        this.syncLock = false;
        this.dealloced = false;
        this.wasNotifiedOfItemsChangeOnServer = false;
        this.opStatus = this.initializeStatus();
    }
    /**
     * If the database has been newly created (because its new or was previously destroyed)
     * we want to reset any sync tokens we have.
     */
    async onNewDatabaseCreated() {
        if (await this.getLastSyncToken()) {
            await this.clearSyncPositionTokens();
        }
    }
    get launchPriorityUuids() {
        var _a;
        return (_a = this.storageService.getValue(StorageKey.LaunchPriorityUuids)) !== null && _a !== void 0 ? _a : [];
    }
    setLaunchPriorityUuids(launchPriorityUuids) {
        this.storageService.setValue(StorageKey.LaunchPriorityUuids, launchPriorityUuids);
    }
    deinit() {
        this.dealloced = true;
        if (this.autoSyncInterval) {
            clearInterval(this.autoSyncInterval);
        }
        ;
        this.autoSyncInterval = undefined;
        this.sessionManager = undefined;
        this.itemManager = undefined;
        this.encryptionService = undefined;
        this.payloadManager = undefined;
        this.storageService = undefined;
        this.apiService = undefined;
        this.opStatus.reset();
        this.opStatus = undefined;
        this.resolveQueue.length = 0;
        this.spawnQueue.length = 0;
        super.deinit();
    }
    initializeStatus() {
        return new SyncOpStatus(setInterval, (event) => {
            void this.notifyEvent(event);
        });
    }
    lockSyncing() {
        this.clientLocked = true;
    }
    unlockSyncing() {
        this.clientLocked = false;
    }
    isOutOfSync() {
        return this.outOfSync;
    }
    getLastSyncDate() {
        return this.lastSyncDate;
    }
    getSyncStatus() {
        return this.opStatus;
    }
    /**
     * Called by application when sign in or registration occurs.
     */
    resetSyncState() {
        this.dirtyIndexAtLastPresyncSave = undefined;
        this.lastSyncDate = undefined;
        this.outOfSync = false;
    }
    isDatabaseLoaded() {
        return this.databaseLoaded;
    }
    async processPriorityItemsForDatabaseLoad(items) {
        if (items.length === 0) {
            return;
        }
        const encryptedPayloads = items.filter(isEncryptedPayload);
        const alreadyDecryptedPayloads = items.filter(isDecryptedPayload);
        const encryptionSplit = SplitPayloadsByEncryptionType(encryptedPayloads);
        const decryptionSplit = CreateDecryptionSplitWithKeyLookup(encryptionSplit);
        const newlyDecryptedPayloads = await this.encryptionService.decryptSplit(decryptionSplit);
        await this.payloadManager.emitPayloads([...alreadyDecryptedPayloads, ...newlyDecryptedPayloads], PayloadEmitSource.LocalDatabaseLoaded);
    }
    async loadDatabasePayloads() {
        this.logger.debug('Loading database payloads');
        if (this.databaseLoaded) {
            throw 'Attempting to initialize already initialized local database.';
        }
        const chunks = await this.device.getDatabaseLoadChunks({
            batchSize: this.options.loadBatchSize,
            contentTypePriority: ContentTypeLocalLoadPriorty,
            uuidPriority: this.launchPriorityUuids,
        }, this.identifier);
        const itemsKeyEntries = isFullEntryLoadChunkResponse(chunks)
            ? chunks.fullEntries.itemsKeys.entries
            : await this.device.getDatabaseEntries(this.identifier, chunks.keys.itemsKeys.keys);
        const keySystemRootKeyEntries = isFullEntryLoadChunkResponse(chunks)
            ? chunks.fullEntries.keySystemRootKeys.entries
            : await this.device.getDatabaseEntries(this.identifier, chunks.keys.keySystemRootKeys.keys);
        const keySystemItemsKeyEntries = isFullEntryLoadChunkResponse(chunks)
            ? chunks.fullEntries.keySystemItemsKeys.entries
            : await this.device.getDatabaseEntries(this.identifier, chunks.keys.keySystemItemsKeys.keys);
        const createPayloadFromEntry = (entry) => {
            try {
                return CreatePayload(entry, PayloadSource.LocalDatabaseLoaded);
            }
            catch (e) {
                console.error('Creating payload failed', e);
                return undefined;
            }
        };
        await this.processPriorityItemsForDatabaseLoad(itemsKeyEntries.map(createPayloadFromEntry).filter(isNotUndefined));
        await this.processPriorityItemsForDatabaseLoad(keySystemRootKeyEntries.map(createPayloadFromEntry).filter(isNotUndefined));
        await this.processPriorityItemsForDatabaseLoad(keySystemItemsKeyEntries.map(createPayloadFromEntry).filter(isNotUndefined));
        /**
         * Map in batches to give interface a chance to update. Note that total decryption
         * time is constant regardless of batch size. Decrypting 3000 items all at once or in
         * batches will result in the same time spent. It's the emitting/painting/rendering
         * that requires batch size optimization.
         */
        const payloadCount = chunks.remainingChunksItemCount;
        let totalProcessedCount = 0;
        const remainingChunks = isFullEntryLoadChunkResponse(chunks)
            ? chunks.fullEntries.remainingChunks
            : chunks.keys.remainingChunks;
        let chunkIndex = 0;
        const ChunkIndexOfContentTypePriorityItems = 0;
        for (const chunk of remainingChunks) {
            const dbEntries = isChunkFullEntry(chunk)
                ? chunk.entries
                : await this.device.getDatabaseEntries(this.identifier, chunk.keys);
            const payloads = dbEntries
                .map((entry) => {
                try {
                    return CreatePayload(entry, PayloadSource.LocalDatabaseLoaded);
                }
                catch (e) {
                    console.error('Creating payload failed', e);
                    return undefined;
                }
            })
                .filter(isNotUndefined);
            await this.processPayloadBatch(payloads, totalProcessedCount, payloadCount);
            const shouldSleepOnlyAfterFirstRegularBatch = chunkIndex > ChunkIndexOfContentTypePriorityItems;
            if (shouldSleepOnlyAfterFirstRegularBatch) {
                await sleep(this.options.sleepBetweenBatches, false, 'Sleeping to allow interface to update');
            }
            totalProcessedCount += payloads.length;
            chunkIndex++;
        }
        this.databaseLoaded = true;
        this.opStatus.setDatabaseLoadStatus(0, 0, true);
    }
    beginAutoSyncTimer() {
        this.autoSyncInterval = setInterval(this.autoSync.bind(this), DEFAULT_AUTO_SYNC_INTERVAL);
    }
    autoSync() {
        if (!this.sockets.isWebSocketConnectionOpen()) {
            this.logger.debug('WebSocket connection is closed, doing autosync');
            void this.sync({ sourceDescription: 'Auto Sync' });
            return;
        }
        if (this.wasNotifiedOfItemsChangeOnServer) {
            this.logger.debug('Was notified of items changed on server, doing autosync');
            this.wasNotifiedOfItemsChangeOnServer = false;
            void this.sync({ sourceDescription: 'WebSockets Event - Items Changed On Server' });
        }
    }
    async processPayloadBatch(batch, currentPosition, payloadCount) {
        this.logger.debug('Processing batch at index', currentPosition, 'length', batch.length);
        const encrypted = [];
        const nonencrypted = [];
        for (const payload of batch) {
            if (isEncryptedPayload(payload)) {
                encrypted.push(payload);
            }
            else {
                nonencrypted.push(payload);
            }
        }
        const encryptionSplit = SplitPayloadsByEncryptionType(encrypted);
        const decryptionSplit = CreateDecryptionSplitWithKeyLookup(encryptionSplit);
        const results = await this.encryptionService.decryptSplit(decryptionSplit);
        await this.payloadManager.emitPayloads([...nonencrypted, ...results], PayloadEmitSource.LocalDatabaseLoaded);
        void this.notifyEvent(SyncEvent.LocalDataIncrementalLoad);
        if (currentPosition != undefined && payloadCount != undefined) {
            this.opStatus.setDatabaseLoadStatus(currentPosition, payloadCount, false);
        }
    }
    setLastSyncToken(token) {
        this.syncToken = token;
        return this.storageService.setValue(StorageKey.LastSyncToken, token);
    }
    async setPaginationToken(token) {
        this.cursorToken = token;
        if (token) {
            return this.storageService.setValue(StorageKey.PaginationToken, token);
        }
        else {
            return this.storageService.removeValue(StorageKey.PaginationToken);
        }
    }
    async getLastSyncToken() {
        if (!this.syncToken) {
            this.syncToken = (await this.storageService.getValue(StorageKey.LastSyncToken));
        }
        return this.syncToken;
    }
    async getPaginationToken() {
        if (!this.cursorToken) {
            this.cursorToken = (await this.storageService.getValue(StorageKey.PaginationToken));
        }
        return this.cursorToken;
    }
    async clearSyncPositionTokens() {
        this.syncToken = undefined;
        this.cursorToken = undefined;
        await this.storageService.removeValue(StorageKey.LastSyncToken);
        await this.storageService.removeValue(StorageKey.PaginationToken);
    }
    itemsNeedingSync() {
        const dirtyItems = this.itemManager.getDirtyItems();
        const itemsWithoutBackoffPenalty = dirtyItems.filter((item) => !this.syncBackoffService.isItemInBackoff(item));
        return itemsWithoutBackoffPenalty;
    }
    async markAllItemsAsNeedingSyncAndPersist() {
        this.logger.debug('Marking all items as needing sync');
        const items = this.itemManager.items;
        const payloads = items.map((item) => {
            return new DecryptedPayload({
                ...item.payload.ejected(),
                dirty: true,
                dirtyIndex: getIncrementedDirtyIndex(),
            });
        });
        await this.payloadManager.emitPayloads(payloads, PayloadEmitSource.LocalChanged);
        /**
         * When signing into an 003 account (or an account that is not the latest), the temporary items key will be 004
         * and will not match user account version, triggering a key not found exception. This error resolves once the
         * download first sync completes and the correct key is downloaded. We suppress any persistence
         * exceptions here to avoid showing an error to the user.
         */
        const hidePersistErrorDueToWaitingOnKeyDownload = true;
        await this.persistPayloads(payloads, { throwError: !hidePersistErrorDueToWaitingOnKeyDownload });
    }
    /**
     * Return the payloads that need local persistence, before beginning a sync.
     * This way, if the application is closed before a sync request completes,
     * pending data will be saved to disk, and synced the next time the app opens.
     */
    popPayloadsNeedingPreSyncSave(from) {
        const lastPreSyncSave = this.dirtyIndexAtLastPresyncSave;
        if (lastPreSyncSave == undefined) {
            return from;
        }
        const payloads = from.filter((candidate) => {
            return !candidate.dirtyIndex || candidate.dirtyIndex > lastPreSyncSave;
        });
        this.dirtyIndexAtLastPresyncSave = getCurrentDirtyIndex();
        return payloads;
    }
    queueStrategyResolveOnNext() {
        return new Promise((resolve, reject) => {
            this.resolveQueue.push({ resolve, reject });
        });
    }
    queueStrategyForceSpawnNew(options) {
        return new Promise((resolve, reject) => {
            this.spawnQueue.push({ resolve, reject, options });
        });
    }
    /**
     * For timing strategy SyncQueueStrategy.ForceSpawnNew, we will execute a whole sync request
     * and pop it from the queue.
     */
    popSpawnQueue() {
        if (this.spawnQueue.length === 0) {
            return null;
        }
        const promise = this.spawnQueue[0];
        removeFromIndex(this.spawnQueue, 0);
        this.logger.debug('Syncing again from spawn queue');
        return this.sync({
            queueStrategy: SyncQueueStrategy.ForceSpawnNew,
            source: SyncSource.SpawnQueue,
            ...promise.options,
        })
            .then(() => {
            promise.resolve();
        })
            .catch(() => {
            promise.reject();
        });
    }
    async payloadsByPreparingForServer(payloads) {
        const payloadSplit = CreatePayloadSplit(payloads);
        const encryptionSplit = SplitPayloadsByEncryptionType(payloadSplit.decrypted);
        const keyLookupSplit = CreateEncryptionSplitWithKeyLookup(encryptionSplit);
        const encryptedResults = await this.encryptionService.encryptSplit(keyLookupSplit);
        const contextPayloads = [
            ...encryptedResults.map(CreateEncryptedServerSyncPushPayload),
            ...payloadSplit.deleted.map(CreateDeletedServerSyncPushPayload),
        ];
        return contextPayloads;
    }
    async downloadFirstSync(waitTimeOnFailureMs, otherSyncOptions) {
        const maxTries = 5;
        for (let i = 0; i < maxTries; i++) {
            await this.sync({
                mode: SyncMode.DownloadFirst,
                queueStrategy: SyncQueueStrategy.ForceSpawnNew,
                source: SyncSource.External,
                ...otherSyncOptions,
            }).catch(console.error);
            if (this.completedOnlineDownloadFirstSync) {
                return;
            }
            else {
                await sleep(waitTimeOnFailureMs);
            }
        }
        console.error(`Failed downloadFirstSync after ${maxTries} tries`);
    }
    async awaitCurrentSyncs() {
        await this.lastSyncInvokationPromise;
        await this.currentSyncRequestPromise;
    }
    async sync(options = {}) {
        if (this.clientLocked) {
            this.logger.debug('Sync locked by client');
            return;
        }
        const fullyResolvedOptions = {
            source: SyncSource.External,
            ...options,
        };
        this.lastSyncInvokationPromise = this.performSync(fullyResolvedOptions);
        return this.lastSyncInvokationPromise;
    }
    async prepareForSync(options) {
        const items = this.itemsNeedingSync();
        /**
         * Freeze the begin date immediately after getting items needing sync. This way an
         * item dirtied at any point after this date is marked as needing another sync
         */
        const beginDate = new Date();
        const frozenDirtyIndex = getCurrentDirtyIndex();
        /**
         * Items that have never been synced and marked as deleted should not be
         * uploaded to server, and instead deleted directly after sync completion.
         */
        const neverSyncedDeleted = items.filter((item) => {
            return item.neverSynced && isDeletedItem(item);
        });
        subtractFromArray(items, neverSyncedDeleted);
        const decryptedPayloads = items.map((item) => {
            return item.payloadRepresentation();
        });
        const payloadsNeedingSave = this.popPayloadsNeedingPreSyncSave(decryptedPayloads);
        const hidePersistErrorDueToWaitingOnKeyDownload = options.mode === SyncMode.DownloadFirst;
        await this.persistPayloads(payloadsNeedingSave, { throwError: !hidePersistErrorDueToWaitingOnKeyDownload });
        if (options.onPresyncSave) {
            options.onPresyncSave();
        }
        return { items, beginDate, frozenDirtyIndex, neverSyncedDeleted };
    }
    /**
     * Allows us to lock this function from triggering duplicate network requests.
     * There are two types of locking checks:
     * 1. syncLocked(): If a call to sync() call has begun preparing to be sent to the server.
     *                  but not yet completed all the code below before reaching that point.
     *                  (before reaching opStatus.setDidBegin).
     * 2. syncOpInProgress: If a sync() call is in flight to the server.
     */
    configureSyncLock(options) {
        const syncInProgress = this.opStatus.syncInProgress;
        const databaseLoaded = this.databaseLoaded;
        const canExecuteSync = !this.syncLock;
        const syncLimitReached = this.syncFrequencyGuard.isSyncCallsThresholdReachedThisMinute();
        const shouldExecuteSync = canExecuteSync && databaseLoaded && !syncInProgress && !syncLimitReached;
        if (shouldExecuteSync) {
            this.syncLock = true;
        }
        else {
            this.logger.debug(!canExecuteSync
                ? 'Another function call has begun preparing for sync.'
                : syncInProgress
                    ? 'Attempting to sync while existing sync in progress.'
                    : 'Attempting to sync before local database has loaded.', options);
        }
        const releaseLock = () => {
            this.syncLock = false;
        };
        return { shouldExecuteSync, releaseLock };
    }
    deferSyncRequest(options) {
        const useStrategy = !isNullOrUndefined(options.queueStrategy)
            ? options.queueStrategy
            : SyncQueueStrategy.ResolveOnNext;
        if (useStrategy === SyncQueueStrategy.ResolveOnNext) {
            return this.queueStrategyResolveOnNext();
        }
        else if (useStrategy === SyncQueueStrategy.ForceSpawnNew) {
            return this.queueStrategyForceSpawnNew(options);
        }
        else {
            throw Error('Unhandled timing strategy');
        }
    }
    async prepareForSyncExecution(items, inTimeResolveQueue, beginDate, frozenDirtyIndex) {
        this.opStatus.setDidBegin();
        await this.notifyEvent(SyncEvent.SyncDidBeginProcessing);
        /**
         * Subtract from array as soon as we're sure they'll be called.
         * resolves are triggered at the end of this function call
         */
        subtractFromArray(this.resolveQueue, inTimeResolveQueue);
        /**
         * lastSyncBegan must be set *after* any point we may have returned above.
         * Setting this value means the item was 100% sent to the server.
         */
        if (items.length > 0) {
            return this.setLastSyncBeganForItems(items, beginDate, frozenDirtyIndex);
        }
        else {
            return items;
        }
    }
    async setLastSyncBeganForItems(itemsToLookupUuidsFor, date, globalDirtyIndex) {
        const uuids = Uuids(itemsToLookupUuidsFor);
        const items = this.itemManager.getCollection().findAll(uuids).filter(isDecryptedOrDeletedItem);
        const payloads = [];
        for (const item of items) {
            const mutator = new ItemMutator(item, MutationType.NonDirtying);
            mutator.setBeginSync(date, globalDirtyIndex);
            const payload = mutator.getResult();
            payloads.push(payload);
        }
        await this.payloadManager.emitPayloads(payloads, PayloadEmitSource.PreSyncSave);
        return this.itemManager.findAnyItems(uuids);
    }
    /**
     * The InTime resolve queue refers to any sync requests that were made while we still
     * have not sent out the current request. So, anything in the InTime resolve queue
     * will have made it "in time" to piggyback on the current request. Anything that comes
     * after InTime will schedule a new sync request.
     */
    getPendingRequestsMadeInTimeToPiggyBackOnCurrentRequest() {
        return this.resolveQueue.slice();
    }
    getOfflineSyncParameters(payloads, mode = SyncMode.Default) {
        const uploadPayloads = mode === SyncMode.Default ? payloads : [];
        return { uploadPayloads };
    }
    createOfflineSyncOperation(payloads, options) {
        this.logger.debug('Syncing offline user', 'source:', SyncSource[options.source], 'sourceDesc', options.sourceDescription, 'mode:', options.mode && SyncMode[options.mode], 'payloads:', payloads);
        const operation = new OfflineSyncOperation(payloads, async (type, response) => {
            if (this.dealloced) {
                return;
            }
            if (type === SyncSignal.Response && response) {
                await this.handleOfflineResponse(response);
            }
        });
        return operation;
    }
    async getOnlineSyncParameters(payloads, mode = SyncMode.Default) {
        const useMode = !this.completedOnlineDownloadFirstSync ? SyncMode.DownloadFirst : mode;
        if (useMode === SyncMode.Default && !this.completedOnlineDownloadFirstSync) {
            throw Error('Attempting to default mode sync without having completed initial.');
        }
        const isReadOnlySession = this.sessionManager.isCurrentSessionReadOnly();
        if (isReadOnlySession) {
            this.logger.debug('Skipping upload payloads because session is read-only.');
            return { uploadPayloads: [], syncMode: useMode };
        }
        const uploadPayloads = useMode === SyncMode.Default ? await this.payloadsByPreparingForServer(payloads) : [];
        return { uploadPayloads, syncMode: useMode };
    }
    async createServerSyncOperation(payloads, options, mode = SyncMode.Default) {
        const syncToken = options.sharedVaultUuids && options.sharedVaultUuids.length > 0 && options.syncSharedVaultsFromScratch
            ? undefined
            : await this.getLastSyncToken();
        const paginationToken = options.sharedVaultUuids && options.syncSharedVaultsFromScratch ? undefined : await this.getPaginationToken();
        const operation = new AccountSyncOperation(payloads, async (type, response, stats) => {
            switch (type) {
                case SyncSignal.Response:
                    if (this.dealloced) {
                        return;
                    }
                    if (response === null || response === void 0 ? void 0 : response.hasError) {
                        this.handleErrorServerResponse(response);
                    }
                    else if (response) {
                        await this.handleSuccessServerResponse(operation, response);
                    }
                    break;
                case SyncSignal.StatusChanged:
                    if (stats) {
                        this.opStatus.setUploadStatus(stats.completedUploadCount, stats.totalUploadCount);
                    }
                    break;
            }
        }, this.apiService, {
            syncToken,
            paginationToken,
            sharedVaultUuids: options.sharedVaultUuids,
        });
        this.logger.debug('Syncing online user', 'source', SyncSource[options.source], 'operation id', operation.id, 'integrity check', options.checkIntegrity, 'mode', SyncMode[mode], 'syncToken', syncToken, 'cursorToken', paginationToken, 'payloads', payloads);
        return operation;
    }
    async createSyncOperation(payloads, online, options) {
        if (online) {
            const { uploadPayloads, syncMode } = await this.getOnlineSyncParameters(payloads, options.mode);
            return {
                operation: await this.createServerSyncOperation(uploadPayloads, options, syncMode),
                mode: syncMode,
            };
        }
        else {
            const { uploadPayloads } = this.getOfflineSyncParameters(payloads, options.mode);
            return {
                operation: this.createOfflineSyncOperation(uploadPayloads, options),
                mode: options.mode || SyncMode.Default,
            };
        }
    }
    async performSync(options) {
        const { shouldExecuteSync, releaseLock } = this.configureSyncLock(options);
        const { items, beginDate, frozenDirtyIndex, neverSyncedDeleted } = await this.prepareForSync(options);
        const shouldSkipUploadsForReadOnlySession = this.sessionManager.isCurrentSessionReadOnly() === true;
        if (options.mode === SyncMode.LocalOnly) {
            this.logger.debug('Syncing local only, skipping remote sync request');
            releaseLock();
            return;
        }
        const inTimeResolveQueue = this.getPendingRequestsMadeInTimeToPiggyBackOnCurrentRequest();
        if (!shouldExecuteSync) {
            return this.deferSyncRequest(options);
        }
        if (this.dealloced) {
            return;
        }
        const latestItems = await this.prepareForSyncExecution(shouldSkipUploadsForReadOnlySession ? [] : items, inTimeResolveQueue, beginDate, frozenDirtyIndex);
        if (shouldSkipUploadsForReadOnlySession && items.length > 0) {
            this.logger.debug('Read-only session detected, skipping upload of dirty items.');
        }
        const online = this.sessionManager.online();
        const { operation, mode: syncMode } = await this.createSyncOperation(latestItems.map((i) => i.payloadRepresentation()), online, options);
        const operationPromise = operation.run();
        this.currentSyncRequestPromise = operationPromise;
        await operationPromise;
        if (this.dealloced) {
            return;
        }
        releaseLock();
        const { hasError } = await this.handleSyncOperationFinish(operation, options, neverSyncedDeleted, syncMode);
        if (hasError) {
            return;
        }
        const didSyncAgain = await this.potentiallySyncAgainAfterSyncCompletion(syncMode, options, inTimeResolveQueue, online);
        if (didSyncAgain) {
            return;
        }
        if (options.checkIntegrity && online) {
            await this.notifyEventSync(SyncEvent.SyncRequestsIntegrityCheck, {
                source: options.source,
            });
        }
        await this.notifyEventSync(SyncEvent.SyncCompletedWithAllItemsUploadedAndDownloaded, {
            source: options.source,
            options,
        });
        this.resolvePendingSyncRequestsThatMadeItInTimeOfCurrentRequest(inTimeResolveQueue);
        return undefined;
    }
    async getRawSyncRequestForExternalUse(items) {
        if (this.dealloced) {
            return;
        }
        const online = this.sessionManager.online();
        if (!online) {
            return;
        }
        const payloads = await this.payloadsByPreparingForServer(items.map((i) => i.payloadRepresentation()));
        const syncToken = await this.getLastSyncToken();
        const paginationToken = await this.getPaginationToken();
        return this.apiService.getSyncHttpRequest(payloads, syncToken, paginationToken, 150);
    }
    async handleOfflineResponse(response) {
        this.logger.debug('Offline Sync Response', response);
        const masterCollection = this.payloadManager.getMasterCollection();
        const delta = new DeltaOfflineSaved(masterCollection, response.savedPayloads);
        const emit = delta.result();
        const payloadsToPersist = await this.payloadManager.emitDeltaEmit(emit);
        await this.persistPayloads(payloadsToPersist);
        this.opStatus.clearError();
        await this.notifyEvent(SyncEvent.PaginatedSyncRequestCompleted, response);
    }
    handleErrorServerResponse(response) {
        var _a;
        this.logger.debug('Sync Error', response);
        if (response.status === INVALID_SESSION_RESPONSE_STATUS) {
            void this.notifyEvent(SyncEvent.InvalidSession);
        }
        if (response.status === TOO_MANY_REQUESTS_RESPONSE_STATUS) {
            void this.notifyEvent(SyncEvent.TooManyRequests);
        }
        (_a = this.opStatus) === null || _a === void 0 ? void 0 : _a.setError(response.error);
        void this.notifyEvent(SyncEvent.SyncError, response);
    }
    async handleSuccessServerResponse(operation, response) {
        if (this._simulate_latency) {
            await sleep(this._simulate_latency.latency);
        }
        this.opStatus.clearError();
        this.opStatus.setDownloadStatus(response.retrievedPayloads.length);
        const masterCollection = this.payloadManager.getMasterCollection();
        const historyMap = this.historyService.getHistoryMapCopy();
        if (response.userEvents && response.userEvents.length > 0) {
            await this.notifyEventSync(SyncEvent.ReceivedNotifications, response.userEvents);
        }
        if (response.asymmetricMessages && response.asymmetricMessages.length > 0) {
            await this.notifyEventSync(SyncEvent.ReceivedAsymmetricMessages, response.asymmetricMessages);
        }
        if (response.vaults && response.vaults.length > 0) {
            await this.notifyEventSync(SyncEvent.ReceivedRemoteSharedVaults, response.vaults);
        }
        if (response.vaultInvites && response.vaultInvites.length > 0) {
            await this.notifyEventSync(SyncEvent.ReceivedSharedVaultInvites, response.vaultInvites);
        }
        const resolver = new ServerSyncResponseResolver({
            retrievedPayloads: await this.processServerPayloads(response.retrievedPayloads, PayloadSource.RemoteRetrieved),
            savedPayloads: response.savedPayloads,
            conflicts: await this.decryptServerConflicts(response.conflicts),
        }, masterCollection, operation.payloadsSavedOrSaving, historyMap);
        this.logger.debug('Online Sync Response', 'Operator ID', operation.id, response.rawResponse.data, 'Decrypted payloads', resolver['payloadSet']);
        const emits = resolver.result();
        for (const emit of emits) {
            const payloadsToPersist = await this.payloadManager.emitDeltaEmit(emit);
            await this.persistPayloads(payloadsToPersist);
        }
        if (!operation.options.sharedVaultUuids) {
            await Promise.all([
                this.setLastSyncToken(response.lastSyncToken),
                this.setPaginationToken(response.paginationToken),
            ]);
        }
        await this.notifyEvent(SyncEvent.PaginatedSyncRequestCompleted, {
            ...response,
            uploadedPayloads: operation.payloads,
            options: operation.options,
        });
    }
    async decryptServerConflicts(conflictMap) {
        const decrypted = {};
        for (const conflictType of Object.keys(conflictMap)) {
            const conflictsForType = conflictMap[conflictType];
            if (!conflictsForType) {
                continue;
            }
            if (!decrypted[conflictType]) {
                decrypted[conflictType] = [];
            }
            const decryptedConflictsForType = decrypted[conflictType];
            if (!decryptedConflictsForType) {
                throw Error('Decrypted conflicts for type should exist');
            }
            for (const conflict of conflictsForType) {
                const decryptedUnsavedItem = conflict.unsaved_item
                    ? await this.processServerPayload(conflict.unsaved_item, PayloadSource.RemoteRetrieved)
                    : undefined;
                const decryptedServerItem = conflict.server_item
                    ? await this.processServerPayload(conflict.server_item, PayloadSource.RemoteRetrieved)
                    : undefined;
                const decryptedEntry = {
                    type: conflict.type,
                    unsaved_item: decryptedUnsavedItem,
                    server_item: decryptedServerItem,
                };
                decryptedConflictsForType.push(decryptedEntry);
            }
        }
        return decrypted;
    }
    async processServerPayload(item, source) {
        const result = await this.processServerPayloads([item], source);
        return result[0];
    }
    async processServerPayloads(items, source) {
        const payloads = items
            .map((i) => {
            const result = CreatePayloadFromRawServerItem(i, source);
            return result.isFailed() ? undefined : result.getValue();
        })
            .filter(isNotUndefined);
        const { encrypted, deleted } = CreateNonDecryptedPayloadSplit(payloads);
        const results = [...deleted];
        const { rootKeyEncryption, itemsKeyEncryption, keySystemRootKeyEncryption } = SplitPayloadsByEncryptionType(encrypted);
        const { results: rootKeyDecryptionResults, map: processedItemsKeys } = await this.decryptServerItemsKeys(rootKeyEncryption || []);
        extendArray(results, rootKeyDecryptionResults);
        const { results: keySystemRootKeyDecryptionResults, map: processedKeySystemItemsKeys } = await this.decryptServerKeySystemItemsKeys(keySystemRootKeyEncryption || []);
        extendArray(results, keySystemRootKeyDecryptionResults);
        if (itemsKeyEncryption) {
            const decryptionResults = await this.decryptProcessedServerPayloads(itemsKeyEncryption, {
                ...processedItemsKeys,
                ...processedKeySystemItemsKeys,
            });
            extendArray(results, decryptionResults);
        }
        return results;
    }
    async decryptServerItemsKeys(payloads) {
        const map = {};
        if (payloads.length === 0) {
            return {
                results: [],
                map,
            };
        }
        const rootKeySplit = {
            usesRootKeyWithKeyLookup: {
                items: payloads,
            },
        };
        const results = await this.encryptionService.decryptSplit(rootKeySplit);
        results.forEach((result) => {
            if (isDecryptedPayload(result) && result.content_type === ContentType.TYPES.ItemsKey) {
                map[result.uuid] = result;
            }
        });
        return {
            results,
            map,
        };
    }
    async decryptServerKeySystemItemsKeys(payloads) {
        const map = {};
        if (payloads.length === 0) {
            return {
                results: [],
                map,
            };
        }
        const keySystemRootKeySplit = {
            usesKeySystemRootKeyWithKeyLookup: {
                items: payloads,
            },
        };
        const results = await this.encryptionService.decryptSplit(keySystemRootKeySplit);
        results.forEach((result) => {
            if (isDecryptedPayload(result) &&
                result.content_type === ContentType.TYPES.KeySystemItemsKey) {
                map[result.uuid] = result;
            }
        });
        return {
            results,
            map,
        };
    }
    async decryptProcessedServerPayloads(payloads, map) {
        return Promise.all(payloads.map(async (encrypted) => {
            const previouslyProcessedItemsKey = map[encrypted.items_key_id];
            const itemsKey = previouslyProcessedItemsKey
                ? CreateDecryptedItemFromPayload(previouslyProcessedItemsKey)
                : undefined;
            const keyedSplit = {};
            if (itemsKey) {
                keyedSplit.usesItemsKey = {
                    items: [encrypted],
                    key: itemsKey,
                };
            }
            else {
                keyedSplit.usesItemsKeyWithKeyLookup = {
                    items: [encrypted],
                };
            }
            return this.encryptionService.decryptSplitSingle(keyedSplit);
        }));
    }
    async handleSyncOperationFinish(operation, options, neverSyncedDeleted, syncMode) {
        this.opStatus.setDidEnd();
        if (this.opStatus.hasError()) {
            return { hasError: true };
        }
        this.opStatus.reset();
        this.lastSyncDate = new Date();
        this.syncFrequencyGuard.incrementCallsPerMinute();
        if (operation instanceof AccountSyncOperation && operation.numberOfItemsInvolved >= this.majorChangeThreshold) {
            void this.notifyEvent(SyncEvent.MajorDataChange);
        }
        if (neverSyncedDeleted.length > 0) {
            await this.handleNeverSyncedDeleted(neverSyncedDeleted);
        }
        if (syncMode !== SyncMode.DownloadFirst) {
            await this.notifyEvent(SyncEvent.SyncCompletedWithAllItemsUploaded, {
                source: options.source,
            });
        }
        return { hasError: false };
    }
    async handleDownloadFirstCompletionAndSyncAgain(online, options) {
        if (online) {
            this.completedOnlineDownloadFirstSync = true;
        }
        await this.notifyEvent(SyncEvent.DownloadFirstSyncCompleted);
        await this.sync({
            source: SyncSource.AfterDownloadFirst,
            checkIntegrity: true,
            awaitAll: options.awaitAll,
        });
    }
    async syncAgainByHandlingRequestsWaitingInResolveQueue(options) {
        this.logger.debug('Syncing again from resolve queue');
        const promise = this.sync({
            source: SyncSource.ResolveQueue,
            checkIntegrity: options.checkIntegrity,
        });
        if (options.awaitAll) {
            await promise;
        }
    }
    /**
     * As part of the just concluded sync operation, more items may have
     * been dirtied (like conflicts), and the caller may want to await the
     * full resolution of these items.
     */
    async syncAgainByHandlingNewDirtyItems(options) {
        await this.sync({
            source: SyncSource.MoreDirtyItems,
            checkIntegrity: options.checkIntegrity,
            awaitAll: options.awaitAll,
        });
    }
    /**
     * For timing strategy SyncQueueStrategy.ResolveOnNext.
     * Execute any callbacks pulled before this sync request began.
     * Calling resolve on the callbacks should be the last thing we do in this function,
     * to simulate calling .sync as if it went through straight to the end without having
     * to be queued.
     */
    resolvePendingSyncRequestsThatMadeItInTimeOfCurrentRequest(inTimeResolveQueue) {
        for (const callback of inTimeResolveQueue) {
            callback.resolve();
        }
    }
    async potentiallySyncAgainAfterSyncCompletion(syncMode, options, inTimeResolveQueue, online) {
        if (syncMode === SyncMode.DownloadFirst) {
            await this.handleDownloadFirstCompletionAndSyncAgain(online, options);
            this.resolvePendingSyncRequestsThatMadeItInTimeOfCurrentRequest(inTimeResolveQueue);
            return true;
        }
        const didSpawnNewRequest = this.popSpawnQueue();
        const resolveQueueHasRequestsThatDidntMakeItInTime = this.resolveQueue.length > 0;
        if (!didSpawnNewRequest && resolveQueueHasRequestsThatDidntMakeItInTime) {
            await this.syncAgainByHandlingRequestsWaitingInResolveQueue(options);
            this.resolvePendingSyncRequestsThatMadeItInTimeOfCurrentRequest(inTimeResolveQueue);
            return true;
        }
        const newItemsNeedingSync = this.itemsNeedingSync();
        if (newItemsNeedingSync.length > 0) {
            await this.syncAgainByHandlingNewDirtyItems(options);
            this.resolvePendingSyncRequestsThatMadeItInTimeOfCurrentRequest(inTimeResolveQueue);
            return true;
        }
        return false;
    }
    /**
     * Items that have never been synced and marked as deleted should be cleared
     * as dirty, mapped, then removed from storage.
     */
    async handleNeverSyncedDeleted(items) {
        const payloads = items.map((item) => {
            return item.payloadRepresentation({
                dirty: false,
            });
        });
        await this.payloadManager.emitPayloads(payloads, PayloadEmitSource.LocalChanged);
        await this.persistPayloads(payloads);
    }
    async persistPayloads(payloads, options = { throwError: true }) {
        if (payloads.length === 0 || this.dealloced) {
            return;
        }
        return this.storageService.savePayloads(payloads).catch((error) => {
            if (options.throwError) {
                void this.notifyEvent(SyncEvent.DatabaseWriteError, error);
                SNLog.error(error);
            }
        });
    }
    setInSync(isInSync) {
        if (isInSync === !this.outOfSync) {
            return;
        }
        if (isInSync) {
            this.outOfSync = false;
            void this.notifyEvent(SyncEvent.ExitOutOfSync);
        }
        else {
            this.outOfSync = true;
            void this.notifyEvent(SyncEvent.EnterOutOfSync);
        }
    }
    async handleEvent(event) {
        switch (event.type) {
            case IntegrityEvent.IntegrityCheckCompleted:
                await this.handleIntegrityCheckEventResponse(event.payload);
                break;
            case WebSocketsServiceEvent.ItemsChangedOnServer:
                this.wasNotifiedOfItemsChangeOnServer = true;
                break;
            default:
                break;
        }
    }
    async handleIntegrityCheckEventResponse(eventPayload) {
        const rawPayloads = eventPayload.rawPayloads;
        if (rawPayloads.length === 0) {
            this.setInSync(true);
            return;
        }
        const rawPayloadsFilteringResult = FilterDisallowedRemotePayloadsAndMap(rawPayloads);
        const receivedPayloads = rawPayloadsFilteringResult.filtered
            .map((rawPayload) => {
            const result = CreatePayloadFromRawServerItem(rawPayload, PayloadSource.RemoteRetrieved);
            if (result.isFailed()) {
                return undefined;
            }
            return result.getValue();
        })
            .filter(isNotUndefined);
        const payloadSplit = CreateNonDecryptedPayloadSplit(receivedPayloads);
        const encryptionSplit = SplitPayloadsByEncryptionType(payloadSplit.encrypted);
        const keyedSplit = CreateDecryptionSplitWithKeyLookup(encryptionSplit);
        const decryptionResults = await this.encryptionService.decryptSplit(keyedSplit);
        this.setInSync(false);
        await this.emitOutOfSyncRemotePayloads([...decryptionResults, ...payloadSplit.deleted]);
        const shouldCheckIntegrityAgainAfterSync = eventPayload.source !== SyncSource.ResolveOutOfSync;
        await this.sync({
            checkIntegrity: shouldCheckIntegrityAgainAfterSync,
            source: SyncSource.ResolveOutOfSync,
        });
    }
    async emitOutOfSyncRemotePayloads(payloads) {
        const delta = new DeltaOutOfSync(this.payloadManager.getMasterCollection(), ImmutablePayloadCollection.WithPayloads(payloads), this.historyService.getHistoryMapCopy());
        const emit = delta.result();
        await this.payloadManager.emitDeltaEmit(emit);
        await this.persistPayloads(emit.emits);
    }
    async syncSharedVaultsFromScratch(sharedVaultUuids) {
        await this.sync({
            sharedVaultUuids: sharedVaultUuids,
            syncSharedVaultsFromScratch: true,
            queueStrategy: SyncQueueStrategy.ForceSpawnNew,
            awaitAll: true,
        });
    }
    /** @e2e_testing */
    // eslint-disable-next-line camelcase
    ut_setDatabaseLoaded(loaded) {
        this.databaseLoaded = loaded;
    }
    /** @e2e_testing */
    // eslint-disable-next-line camelcase
    ut_clearLastSyncDate() {
        this.lastSyncDate = undefined;
    }
    /** @e2e_testing */
    // eslint-disable-next-line camelcase
    ut_beginLatencySimulator(latency) {
        this._simulate_latency = {
            latency: latency || 1000,
            enabled: true,
        };
    }
    /** @e2e_testing */
    // eslint-disable-next-line camelcase
    ut_endLatencySimulator() {
        this._simulate_latency = undefined;
    }
}
